/**
 * OrgProvider - Organization Context for V2 Dashboard
 *
 * Manages the current organization selection and org-scoped data for V2 components.
 * Fetches data directly from vb-server API (no Redux dependency).
 *
 * Priority for org selection:
 * 1. URL parameter (e.g., /v2/organizations/:orgId/...)
 * 2. localStorage (persisted selection across sessions)
 * 3. First org from user's accessible orgs
 *
 * Usage:
 *   const { currentOrg, orgs, channels, setCurrentOrg, refreshChannels } = useOrg();
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { AuthOrg } from '../services/auth';
import { listChannels, Channel } from '../../api/server/channels';
import { apiErrorMessage } from '../../api/server/client';
import {
  listGroups,
  type Group,
  getFilterSettings,
  type FilterVisibility
} from '../../api/server/groups';
import { logger } from '../../utils/logger';

// Storage key for persisted org selection
const CURRENT_ORG_KEY = 'vb_current_org_id';

const FILTER_VISIBILITY_DEFAULTS: FilterVisibility = {
  channel_id: true,
  source_type: false,
  device_id: true,
  talkgroup_tag: true,
  radio_id: true,
  speaker_id: true,
  tag: true
};

interface OrgContextType {
  /** Currently selected organization */
  currentOrg: AuthOrg | null;
  /** All organizations the user has access to */
  orgs: AuthOrg[];
  /** Map of orgs by org_id for quick lookup */
  orgsMap: Record<string, AuthOrg>;
  /** Channels for the current organization */
  channels: Channel[];
  /** Map of channels by channel_id for quick lookup */
  channelsMap: Record<string, Channel>;
  /** Groups for the current organization */
  groups: Group[];
  /** Map of groups by group_id for quick lookup */
  groupsMap: Record<string, Group>;
  /** Filter visibility settings for the current org */
  filterVisibility: FilterVisibility;
  /** Whether org data is loading */
  isLoading: boolean;
  /** Whether channels are loading */
  isLoadingChannels: boolean;
  /** User-facing error from the last channel load (e.g. rate limiting); null when healthy. */
  channelsError: string | null;
  /** Set current organization (navigates and persists) */
  setCurrentOrg: (orgId: string) => void;
  /** Refresh channels from server */
  refreshChannels: () => Promise<void>;
  /** Refresh groups from server */
  refreshGroups: () => Promise<void>;
  /** Refresh filter settings from server */
  refreshFilterSettings: () => Promise<void>;
  /** Refresh org list (re-fetches auth user data) */
  refreshOrgs: () => Promise<void>;
}

// Export for test utilities
export const OrgContext = createContext<OrgContextType | undefined>(undefined);

interface OrgProviderProps {
  children: React.ReactNode;
}

export function OrgProvider({ children }: OrgProviderProps) {
  const { user, isLoading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ orgId?: string }>();

  // Track previous org to detect changes
  const prevOrgIdRef = useRef<string | null>(null);

  // Get org from URL if present
  const urlOrgId = params.orgId;

  // Local state for current org
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(() => {
    // Initialize from localStorage (persists across browser sessions)
    return localStorage.getItem(CURRENT_ORG_KEY);
  });

  // Channels state
  const [channels, setChannels] = useState<Channel[]>([]);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [channelsError, setChannelsError] = useState<string | null>(null);

  // Groups state
  const [groups, setGroups] = useState<Group[]>([]);

  // Filter visibility state
  const [filterVisibility, setFilterVisibility] = useState<FilterVisibility>(
    FILTER_VISIBILITY_DEFAULTS
  );

  // Derive orgs from user data
  const orgsMap = useMemo(() => user?.orgs || {}, [user]);
  const orgs = useMemo(() => Object.values(orgsMap), [orgsMap]);

  // Channels map for quick lookup
  const channelsMap = useMemo(() => {
    const map: Record<string, Channel> = {};
    for (const channel of channels) {
      map[channel.channel_id] = channel;
    }
    return map;
  }, [channels]);

  // Groups map for quick lookup
  const groupsMap = useMemo(() => {
    const map: Record<string, Group> = {};
    for (const group of groups) {
      map[group.group_id] = group;
    }
    return map;
  }, [groups]);

  // Determine effective org ID (URL > state > localStorage > first org)
  const effectiveOrgId = useMemo(() => {
    // 1. URL param takes priority
    if (urlOrgId && orgsMap[urlOrgId]) {
      return urlOrgId;
    }
    // 2. Current state/localStorage selection
    if (currentOrgId && orgsMap[currentOrgId]) {
      return currentOrgId;
    }
    // 3. First available org
    const firstOrgId = user?.orgIds?.[0];
    if (firstOrgId && orgsMap[firstOrgId]) {
      return firstOrgId;
    }
    return null;
  }, [urlOrgId, currentOrgId, orgsMap, user?.orgIds]);

  // Current org object
  const currentOrg = useMemo(() => {
    return effectiveOrgId ? orgsMap[effectiveOrgId] || null : null;
  }, [effectiveOrgId, orgsMap]);

  // Persist to localStorage when effective org changes
  useEffect(() => {
    if (effectiveOrgId) {
      localStorage.setItem(CURRENT_ORG_KEY, effectiveOrgId);
      setCurrentOrgId(effectiveOrgId);
    }
  }, [effectiveOrgId]);

  // Fetch channels from vb-server
  const fetchChannels = useCallback(async (orgId: string) => {
    setIsLoadingChannels(true);
    setChannelsError(null);
    try {
      const channelsList = await listChannels(orgId, { includeInactive: true });
      setChannels(channelsList);
      logger.debug('[OrgProvider] Channels loaded:', channelsList.length);
    } catch (error) {
      logger.error('[OrgProvider] Failed to load channels:', error);
      setChannels([]);
      setChannelsError(apiErrorMessage(error, 'Failed to load channels.'));
    } finally {
      setIsLoadingChannels(false);
    }
  }, []);

  // Fetch groups from vb-server
  const fetchGroupsData = useCallback(async (orgId: string) => {
    try {
      const result = await listGroups(orgId);
      setGroups(result);
      logger.debug('[OrgProvider] Groups loaded:', result.length);
    } catch (error) {
      logger.error('[OrgProvider] Failed to load groups:', error);
      setGroups([]);
    }
  }, []);

  // Fetch filter settings from vb-server
  const fetchFilterSettings = useCallback(async (orgId: string) => {
    try {
      const result = await getFilterSettings(orgId);
      setFilterVisibility({ ...FILTER_VISIBILITY_DEFAULTS, ...result.filter_visibility });
      logger.debug('[OrgProvider] Filter settings loaded');
    } catch (error) {
      logger.error('[OrgProvider] Failed to load filter settings:', error);
      setFilterVisibility(FILTER_VISIBILITY_DEFAULTS);
    }
  }, []);

  // Refresh channels (exposed to context consumers)
  const refreshChannels = useCallback(async () => {
    if (currentOrg?.org_id) {
      await fetchChannels(currentOrg.org_id);
    }
  }, [currentOrg?.org_id, fetchChannels]);

  // Refresh groups (exposed to context consumers)
  const refreshGroups = useCallback(async () => {
    if (currentOrg?.org_id) {
      await fetchGroupsData(currentOrg.org_id);
    }
  }, [currentOrg?.org_id, fetchGroupsData]);

  // Refresh filter settings (exposed to context consumers)
  const refreshFilterSettings = useCallback(async () => {
    if (currentOrg?.org_id) {
      await fetchFilterSettings(currentOrg.org_id);
    }
  }, [currentOrg?.org_id, fetchFilterSettings]);

  // Load org data (channels) when org changes
  useEffect(() => {
    if (!currentOrg?.org_id) return;

    // Only trigger if org actually changed
    if (prevOrgIdRef.current === currentOrg.org_id) return;
    prevOrgIdRef.current = currentOrg.org_id;

    logger.info('[OrgProvider] Loading org data for:', currentOrg.name);

    // Fetch channels, groups, and filter settings from vb-server
    fetchChannels(currentOrg.org_id);
    fetchGroupsData(currentOrg.org_id);
    fetchFilterSettings(currentOrg.org_id);
  }, [currentOrg, fetchChannels, fetchGroupsData, fetchFilterSettings]);

  // Set current organization
  const setCurrentOrg = useCallback(
    (orgId: string) => {
      // Always persist selection — orgsMap may not include the org yet
      // (e.g., after reactivation, before the next render refreshes the map)
      localStorage.setItem(CURRENT_ORG_KEY, orgId);
      setCurrentOrgId(orgId);

      // If we're on an org-specific route, navigate to the new org
      // Clear search params (filter state) — they're org-scoped and stale after switching
      if (urlOrgId && location.pathname.includes(`/organizations/${urlOrgId}`)) {
        const newPath = location.pathname.replace(
          `/organizations/${urlOrgId}`,
          `/organizations/${orgId}`
        );
        navigate({ pathname: newPath, search: '' }, { replace: true });
      }

      if (orgsMap[orgId]) {
        logger.info('[OrgProvider] Switched to org:', orgsMap[orgId].name);
      } else {
        logger.info('[OrgProvider] Pre-selected org (pending refresh):', orgId);
      }
    },
    [orgsMap, urlOrgId, location.pathname, navigate]
  );

  const refreshOrgs = useCallback(async () => {
    await refreshUser();
  }, [refreshUser]);

  const value = useMemo<OrgContextType>(
    () => ({
      currentOrg,
      orgs,
      orgsMap,
      channels,
      channelsMap,
      groups,
      groupsMap,
      filterVisibility,
      isLoading: authLoading,
      isLoadingChannels,
      channelsError,
      setCurrentOrg,
      refreshChannels,
      refreshGroups,
      refreshFilterSettings,
      refreshOrgs
    }),
    [
      currentOrg,
      orgs,
      orgsMap,
      channels,
      channelsMap,
      groups,
      groupsMap,
      filterVisibility,
      authLoading,
      isLoadingChannels,
      channelsError,
      setCurrentOrg,
      refreshChannels,
      refreshGroups,
      refreshFilterSettings,
      refreshOrgs
    ]
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

/**
 * Hook to access organization context
 */
export function useOrg(): OrgContextType {
  const context = useContext(OrgContext);
  if (context === undefined) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
}

export default OrgProvider;
